import { z } from 'zod';
import { PolygonSchema } from './geojson';
import { IncidentHazardTypeSchema } from './incident';

/**
 * Operator-declared EVACUATION ORDER (#445-(3), migration 0115) — the municipal
 * EOC seat's hard NO-GO closer.
 *
 * A first-class operator evac-order the COP RECORDS / COORDINATES on behalf of a
 * NAMED issuing authority. Distinct from the EXTERNAL GeoBC evac layer
 * (`arcgis-feature.ts` / `evac-link.ts`, the read-only ingest) — this is the
 * operator's OWN order so a municipality / First Nation Chief & Council / province
 * can issue + coordinate an evacuation in-COP instead of reverting to paper.
 *
 * AUTHORITY MODEL (research-grounded): the ordering authority in Canada is LOCAL
 * (head-of-council; on-reserve the Chief & Council) under a provincial statute; the
 * province orders directly only via a declared emergency / in the Far North; the
 * federal role is funding/assets, NOT ordering. So there is NO single seat to gate
 * to — instead a MANDATORY structured `issuing_authority_type` + `issuing_authority_name`.
 * The COP records ON BEHALF OF the named body; it never confers the legal power to
 * evacuate. `first_nation` is FIRST-CLASS (its own literal, never folded into 'other').
 * The RECORDER (`created_by_operator_id`) is ORTHOGONAL to the issuer.
 */

const UUID = z.string().uuid();

/** The KIND of body that issued the order. `first_nation` is first-class. */
export const ISSUING_AUTHORITY_TYPES = [
  'municipal',
  'first_nation',
  'provincial',
  'federal',
  'other',
] as const;
export const IssuingAuthorityTypeSchema = z.enum(ISSUING_AUTHORITY_TYPES);
export type IssuingAuthorityType = z.infer<typeof IssuingAuthorityTypeSchema>;

/** Operator-facing labels for the issuing-authority-type dropdown. */
export const ISSUING_AUTHORITY_TYPE_LABELS: Record<IssuingAuthorityType, string> = {
  municipal: 'Municipality',
  first_nation: 'First Nation',
  provincial: 'Province / Territory',
  federal: 'Federal',
  other: 'Other',
};

/**
 * #637 — FIRST NATION evacuation is a REQUEST to the PEOC, not an ORDER issued by an
 * authority. On a First Nation reserve there is NO evacuation order and NO single issuer:
 *  - PERP §6.6.3.1 / §6.6.3.3 (verbatim): a provincial declaration + any emergency orders
 *    made under it DO NOT apply to First Nations reserves (not subject to the EMCPA);
 *    EMCPA s.7.0.2(4) evacuation orders are ONLY applicable to municipalities + unorganized
 *    communities. There is no reserve "evacuation order" to attribute.
 *  - ISC Ontario Region Emergency Management Plan (2022 Indigenous-led approach, verbatim):
 *    "the decision to evacuate a First Nation community must be based on … a CONSENSUS of
 *    opinion between the First Nation Chief and Council, ISC and a present or imminent
 *    event"; "When a First Nation REQUESTS evacuation, the PEOC COORDINATES …" — a JOINT
 *    COMMAND CALL led by the community, not a single issuer.
 *  - Indian Act s.2(3)(b) (verbatim): a band-council power is exercised only "pursuant to
 *    the consent of a majority of the councillors" — COLLECTIVE; never "Chief declares".
 *  - PERP §6.7.1 (verbatim): "First Nations requesting assistance from the PEOC should
 *    include ISC on the request." → ISC is on the request BY DEFAULT (never opt-in).
 *  - PERP fn.26 (verbatim): a First Nation "does not need to declare a formal emergency to
 *    receive assistance" → the request is NEVER gated on a declaration existing.
 * The Band Council Resolution (BCR) is the DECLARATION instrument (#517 / migration 0126),
 * NEVER an evacuation record (PERP §6.6.4). These strings are correct-domain-verbatim
 * (PERP · ISC · Indian Act) — doctrine-reference §4.3; NO §4.3 founder exception is claimed.
 * RECORDS-NOT-CONFERS.
 */
export function isFirstNationEvacuation(issuingAuthorityType: string): boolean {
  return issuingAuthorityType === 'first_nation';
}

/** #637 — the First Nation evacuation-REQUEST primary action label (record button + form
 *  header + submit). PEOC is the RECIPIENT (never authorizer); ISC is included BY DEFAULT. */
export const FN_EVAC_REQUEST_ACTION_LABEL =
  'Record evacuation request — submitted to PEOC (ISC included)';
/** #637 — the register badge that REPLACES the alert/order badge on a First Nation record. */
export const FN_EVAC_REQUEST_BADGE = 'Evacuation Request';
/** #637 — the recipient the request is submitted TO. PEOC as RECIPIENT of the request
 *  (PERP §6.7.1), never an AUTHORIZER of the evacuation. */
export const FN_EVAC_REQUEST_RECIPIENT =
  'the Provincial Emergency Operations Centre (ISC included)';
/** #637 — the OPTIONAL free-text decision-provenance label. NEVER an "issuer": the decision
 *  is a Joint Command Call (ISC Ontario Region EM Plan), recorded, not conferred. */
export const FN_EVAC_DECISION_LABEL =
  'Evacuation decision — Joint Command Call (Chief and Council with ISC)';
/** #637 — the absent state: a FACTUAL absence. The COP may raise an alarm, never lower one,
 *  so this is NEVER "not required" / "no evacuation needed" / an all-clear. */
export const FN_EVAC_ABSENT_LABEL = 'No evacuation request recorded';
/** #637 — the FN variant of the evac→public-alert note. The shared
 *  PUBLIC_ALERT_REQUEST_FROM_EVAC_NOTE names "this order's issuing authority", which does
 *  not exist on a reserve — this names the First Nation and keeps "order" off the surface. */
export const FN_EVAC_PUBLIC_ALERT_NOTE =
  'A public alert is issued only by the PEOC (PERP §6). Requesting one here records the request on behalf of the First Nation and routes it to the PEOC — it does not issue the alert.';

/**
 * "issued by <name> (First Nation)" — the operator-facing attribution for an
 * operator-declared evac order, routing the raw `issuing_authority_type` enum through
 * {@link ISSUING_AUTHORITY_TYPE_LABELS} so a SITREP line never leaks `first_nation:<name>`
 * (W6 raw-enum legibility, ledger §3.D). Accepts a plain string (SITREP read shapes carry
 * the type as `z.string()`); an unrecognised value falls back to the humanized "Other"
 * label — never the raw snake_case token. USES the existing map — it is NOT a second map.
 *
 * #637 — a `first_nation` evacuation is a REQUEST to the PEOC, not an order issued by a
 * named authority. There is NO issuer on a reserve, so this NEVER reads "issued by" for a
 * First Nation: it returns the request framing (PEOC as RECIPIENT, ISC included by default),
 * and deliberately does NOT echo `name` as an issuer. This is the single seam every read
 * surface (incl. the SITREP evac line) routes through, so the reframe reaches them all.
 */
export function formatIssuingAuthority(type: string, name: string): string {
  if (isFirstNationEvacuation(type)) {
    return 'evacuation request submitted to the PEOC (ISC included)';
  }
  const label =
    ISSUING_AUTHORITY_TYPE_LABELS[type as IssuingAuthorityType] ?? ISSUING_AUTHORITY_TYPE_LABELS.other;
  return `issued by ${name} (${label})`;
}

/**
 * #458 — "coordinated by <name>" — the SECONDARY, support-register attribution for the
 * body that COORDINATES an operator-declared evac order in support of the issuer (the
 * Far-North tripartite case: a Band ISSUES; the PEOC COORDINATES). Ontario IMS §4.2:
 * coordination ≠ command — so this is a past-participle VERB attribution ("coordinated
 * by"), NEVER a "coordinating authority" NOUN that would elevate the coordinator to a
 * peer of the issuer. Deliberately name-ONLY (no type parenthetical): it is rendered
 * AFTER {@link formatIssuingAuthority} and must never read as prominent-as/above it
 * (FN-dignity acceptance gate). Returns null when there is no coordinator.
 */
export function formatCoordinatingAuthority(name: string | null | undefined): string | null {
  const trimmed = (name ?? '').trim();
  return trimmed.length > 0 ? `coordinated by ${trimmed}` : null;
}

/**
 * Map a community's `community_type` (the `community.ts` enum) to the DEFAULT
 * issuing-authority type for an evacuation order it declares. First Nation /
 * Inuit / Métis communities issue as `first_nation`; a municipality as
 * `municipal`; everything else falls to `other`. The operator can override in
 * the form — this is only the pre-seed. Shared by the one-click (#457) and the
 * hand-draw paths so the mapping lives in ONE place.
 */
export function communityTypeToIssuingAuthorityType(communityType: string): IssuingAuthorityType {
  switch (communityType) {
    case 'first_nation':
    case 'inuit_community':
    case 'metis_settlement':
      return 'first_nation';
    case 'municipality':
      return 'municipal';
    default:
      return 'other';
  }
}

/** Register: 'alert' (be ready to leave) → 'order' (mandatory leave NOW). */
export const EVAC_ORDER_LEVELS = ['alert', 'order'] as const;
export const EvacOrderLevelSchema = z.enum(EVAC_ORDER_LEVELS);
export type EvacOrderLevel = z.infer<typeof EvacOrderLevelSchema>;

/** Lifecycle: 'active' (in force) | 'rescinded' (lifted). */
export const EvacOrderStatusSchema = z.enum(['active', 'rescinded']);
export type EvacOrderStatus = z.infer<typeof EvacOrderStatusSchema>;

/**
 * A GeoJSON Polygon ring (the evacuation area). As with sectors, PostGIS
 * ST_IsValid rejects self-intersection server-side; here we only enforce the
 * structural minimum (a linear ring needs ≥ 4 positions — 3 distinct + closing).
 */
export const EvacOrderPolygonSchema = PolygonSchema.refine(
  (g) => g.coordinates.length > 0 && g.coordinates[0]!.length >= 4,
  { message: 'polygon outer ring needs at least 4 positions (a closed triangle)' },
);

/* ------------------------------------------------------------------ *
 * Declare — POST /api/evacuation-orders
 * ------------------------------------------------------------------ */

export const OperatorEvacuationOrderCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  level: EvacOrderLevelSchema,
  /** Operator-drawn GeoJSON Polygon (lon,lat rings). */
  geometry: EvacOrderPolygonSchema,
  /** MANDATORY — the COP records on behalf of this authority; rejects if missing. */
  issuing_authority_type: IssuingAuthorityTypeSchema,
  issuing_authority_name: z.string().min(1).max(200),
  /** #458 — OPTIONAL coordinating authority (the Far-North tripartite case: a Band
   *  ISSUES, the PEOC COORDINATES). SECONDARY to the issuer; §4.2 coordination ≠ command. */
  coordinating_authority_type: IssuingAuthorityTypeSchema.optional(),
  coordinating_authority_name: z.string().min(1).max(200).optional(),
  /** Operator-supplied area label (optional). */
  name: z.string().max(200).optional(),
  incident_id: UUID.optional(),
  community_id: UUID.optional(),
  population_estimate: z.number().int().min(0).max(100_000_000).optional(),
  notes: z.string().max(2000).optional(),
});
export type OperatorEvacuationOrderCreate = z.infer<typeof OperatorEvacuationOrderCreateSchema>;

/* ------------------------------------------------------------------ *
 * #459 — CORRECT an existing order (mirrors the sectors PATCH pattern).
 * ------------------------------------------------------------------ */

/** PATCH /api/evacuation-orders/:id/geometry — operator redraws the evacuation area. */
export const OperatorEvacuationOrderGeometryPatchSchema = z.object({
  session_id: z.string().min(1).max(128),
  geometry: EvacOrderPolygonSchema,
});
export type OperatorEvacuationOrderGeometryPatch = z.infer<
  typeof OperatorEvacuationOrderGeometryPatchSchema
>;

/**
 * PATCH /api/evacuation-orders/:id/metadata — correct the record (≥ 1 field). Pass null
 * to CLEAR a nullable field (area name / population / notes / coordinating attribution);
 * omit to leave it unchanged. The issuing authority (type + name) is NOT NULL — it can be
 * corrected but never cleared. A metadata edit is NOT a rescind (the lifecycle is intact).
 */
export const OperatorEvacuationOrderMetadataPatchSchema = z
  .object({
    session_id: z.string().min(1).max(128),
    name: z.string().max(200).nullable().optional(),
    population_estimate: z.number().int().min(0).max(100_000_000).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    /** Correct the ISSUER (NOT NULL — updatable, never clearable). */
    issuing_authority_type: IssuingAuthorityTypeSchema.optional(),
    issuing_authority_name: z.string().min(1).max(200).optional(),
    /** Correct / add / clear the COORDINATOR (nullable — pass null to clear). */
    coordinating_authority_type: IssuingAuthorityTypeSchema.nullable().optional(),
    coordinating_authority_name: z.string().max(200).nullable().optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.population_estimate !== undefined ||
      v.notes !== undefined ||
      v.issuing_authority_type !== undefined ||
      v.issuing_authority_name !== undefined ||
      v.coordinating_authority_type !== undefined ||
      v.coordinating_authority_name !== undefined,
    { message: 'at least one editable field is required' },
  );
export type OperatorEvacuationOrderMetadataPatch = z.infer<
  typeof OperatorEvacuationOrderMetadataPatchSchema
>;

/* ------------------------------------------------------------------ *
 * #457 — one-click "evacuate this community" derived zone.
 * GET /api/communities/:id/evacuation-zone → the evacuation polygon
 * generated FROM the community's OWN geometry (the municipal seat's PRIMARY
 * evac path, instead of hand-drawing), returned WITH provenance so the
 * operator sees where it came from and can adjust before issuing.
 * ------------------------------------------------------------------ */

/** How the one-click evacuation zone was derived, best → last-resort. */
export const COMMUNITY_EVAC_ZONE_SOURCES = [
  'service_area', // the community's own service-area polygon (most precise)
  'populated_place', // a matched StatCan community boundary (#448 populated_places)
  'radius', // a buffer around the community centre (no boundary on file)
] as const;
export const CommunityEvacZoneSourceSchema = z.enum(COMMUNITY_EVAC_ZONE_SOURCES);
export type CommunityEvacZoneSource = z.infer<typeof CommunityEvacZoneSourceSchema>;

export const CommunityEvacuationZoneSchema = z.object({
  community: z.object({
    id: z.string(),
    name: z.string(),
    community_type: z.string(),
  }),
  /** The derived evacuation polygon (single outer ring, lon,lat; ready to declare). */
  geometry: EvacOrderPolygonSchema,
  /** Which tier produced the geometry. */
  source: CommunityEvacZoneSourceSchema,
  /** For `populated_place`: the matched StatCan boundary name / kind (else null). */
  place_name: z.string().nullable(),
  place_kind: z.string().nullable(),
  /** For `radius`: the buffer radius in metres (else null). */
  radius_m: z.number().nullable(),
});
export type CommunityEvacuationZone = z.infer<typeof CommunityEvacuationZoneSchema>;

/** POST /api/evacuation-orders/:id/escalate — ALERT → ORDER. */
export const OperatorEvacuationOrderEscalateSchema = z.object({
  session_id: z.string().min(1).max(128),
});
export type OperatorEvacuationOrderEscalate = z.infer<
  typeof OperatorEvacuationOrderEscalateSchema
>;

/** POST /api/evacuation-orders/:id/rescind — lift the order. */
export const OperatorEvacuationOrderRescindSchema = z.object({
  session_id: z.string().min(1).max(128),
  reason: z.string().max(2000).optional(),
});
export type OperatorEvacuationOrderRescind = z.infer<typeof OperatorEvacuationOrderRescindSchema>;

/**
 * #462 — POST /api/evacuation-orders/:id/start-accounting — begin the §4.30 post-order
 * evacuation ACCOUNTING for this order by establishing the PERSISTED order→incident link
 * the rollup keys on. The route PREFERS attaching the order to the single proximate OPEN
 * hazard incident it responds to (the flood/fire the evacuation is a protective action
 * FOR — avoids inventing a hazard_type); only if none exists does it ORIGINATE a new
 * incident from the order, using `hazard_type` (operator-picked; defaults 'other'). The
 * hazard is used ONLY on the originate path. Idempotent — a second call returns the
 * already-linked incident. `hazard_type` is optional so the attach path needs nothing. */
export const OperatorEvacuationOrderStartAccountingSchema = z.object({
  session_id: z.string().min(1).max(128),
  hazard_type: IncidentHazardTypeSchema.optional(),
});
export type OperatorEvacuationOrderStartAccounting = z.infer<
  typeof OperatorEvacuationOrderStartAccountingSchema
>;

/** #462 — the start-accounting response: the updated order feature (incident_id now
 *  persisted) + how the link was resolved. `mode`: 'existing' (already linked, idempotent
 *  no-op) | 'attached' (linked to a proximate open incident) | 'created' (a new incident
 *  was originated from the order). */
export const OperatorEvacuationOrderStartAccountingResultSchema = z.object({
  order: z.unknown(), // OperatorEvacOrderFeature — kept loose to avoid a schema import cycle
  accounting_incident_id: z.string(),
  incident_number: z.string().nullable(),
  mode: z.enum(['existing', 'attached', 'created']),
});
export type OperatorEvacuationOrderStartAccountingResult = z.infer<
  typeof OperatorEvacuationOrderStartAccountingResultSchema
>;

/* ------------------------------------------------------------------ *
 * Read shapes — served as GeoJSON so the map layer can load directly
 * ------------------------------------------------------------------ */

/** The feature properties carried on every order (drives the render + detail body). */
export const OperatorEvacOrderPropertiesSchema = z.object({
  id: z.string(),
  level: EvacOrderLevelSchema,
  status: EvacOrderStatusSchema,
  name: z.string().nullable(),
  issuing_authority_type: IssuingAuthorityTypeSchema,
  issuing_authority_name: z.string(),
  /** #458 — the coordinating authority (nullable; only the Far-North tripartite case).
   *  SECONDARY to the issuer — rendered "Coordinated by <name>" AFTER it, never above. */
  coordinating_authority_type: IssuingAuthorityTypeSchema.nullable(),
  coordinating_authority_name: z.string().nullable(),
  incident_id: z.string().nullable(),
  /** R2/#478 link provenance (migration 0119): 'manual' = operator-linked via the
   *  dropdown (incident_id set) | 'auto' = the read-time auto-link projection | null =
   *  unlinked. (DB 'detached' never surfaces as a link.) */
  incident_link_mode: z.enum(['manual', 'auto']).nullable().optional(),
  community_id: z.string().nullable(),
  population_estimate: z.number().int().nullable(),
  notes: z.string().nullable(),
  /** The recorder (the operator who declared it in-COP) — ORTHOGONAL to the issuer. */
  created_by_operator_id: z.string().nullable(),
  declared_at: z.string(),
  escalated_at: z.string().nullable(),
  rescinded_at: z.string().nullable(),
});
export type OperatorEvacOrderProperties = z.infer<typeof OperatorEvacOrderPropertiesSchema>;

/** A single order as a GeoJSON Feature (Polygon geometry + properties). */
export const OperatorEvacOrderFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string(),
  geometry: PolygonSchema,
  properties: OperatorEvacOrderPropertiesSchema,
});
export type OperatorEvacOrderFeature = z.infer<typeof OperatorEvacOrderFeatureSchema>;

/** GET /api/evacuation-orders — a FeatureCollection of active orders in the bbox. */
export const OperatorEvacOrderFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(OperatorEvacOrderFeatureSchema),
});
export type OperatorEvacOrderFeatureCollection = z.infer<
  typeof OperatorEvacOrderFeatureCollectionSchema
>;
